import React, { useEffect, useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppSettings } from "../../config/app-settings.js";
import { useDispatch } from "react-redux";
import useRedirectIfAuthenticated from "../../hooks/useRedirectIfAuthenticated.js";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { login } from "../../app/auth/authSlice.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import APIs from "../../Constants/apis.js";
import userDataService from "../../services/userDataService.js";
import FloatingPasswordInput from "../../components/CustomComponents/Input/FloatingPasswordInput.jsx";
import { getPostLoginPath } from "../../utils/authRedirect";
import {
  normalizeSubscriptionAccessFromApi,
  resolveSubscriptionAccess,
} from "../../utils/subscriptionAccess";

function Login() {
  const context = useContext(AppSettings);
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [keepMeLogin, setKeepMeLogin] = useState(false);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isAuthenticated = useRedirectIfAuthenticated();

  // Load keepMeLogin preference from localStorage on component mount
  useEffect(() => {
    const savedKeepMeLogin = localStorage.getItem("keepMeLogin");
    if (savedKeepMeLogin !== null) {
      setKeepMeLogin(JSON.parse(savedKeepMeLogin));
    }
  }, []);

  useEffect(() => {
    // Hide chrome on the login screen only; App restores layout after navigation.
    context.handleSetAppSidebarNone(true);
    context.handleSetAppHeaderNone(true);
    context.handleSetAppContentClass("p-0");
  }, [context]);

  useEffect(() => {
    if (searchParams.get("expired") === "true" && !isAuthenticated) {
      showErrorToast("Session expired. Please sign in again.");
    }
  }, [searchParams, isAuthenticated]);

  const validateForm = () => {
    let valid = true;
    let errors = { userName: "", password: "" };

    if (!userName.trim()) {
      errors.userName = "Email is required";
      valid = false;
    }
    if (!password.trim()) {
      errors.password = "Password is required";
      valid = false;
    }

    if (!valid) {
      showErrorToast(errors.userName || errors.password);
    }

    return valid;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      await axiosInstance.get(APIs.LOGIN, {
        params: {
          userName,
          password,
          keepMeLogin
        }
      }).then(async response => {

        if (response.data.status) {
          const data = response.data.data || {};
          const authToken = data.token || data.Token;
          const staffUser = data.userData || data.UserData;
          const refreshToken = data.refreshToken || data.RefreshToken;
          const apiSubscription =
            data.subscriptionAccess ?? data.SubscriptionAccess;
          const apiSubscriptionAccess = normalizeSubscriptionAccessFromApi(apiSubscription);

          if (!authToken || !staffUser) {
            showErrorToast("Login succeeded but session data was incomplete. Please try again.");
            setLoading(false);
            return;
          }

          // Persist session BEFORE profile/settings calls so axios can attach the new JWT.
          // (Previously fetchUserData ran first → 401 → forceLogout → bounced back to login.)
          const initialUserData = {
            token: authToken,
            userData: staffUser,
            refreshToken: refreshToken || null,
            keepMeLogin: keepMeLogin,
            subscriptionAccessFromApi: apiSubscriptionAccess,
            subscriptionAccess: apiSubscriptionAccess,
          };

          if (refreshToken) {
            sessionStorage.setItem("refreshToken", refreshToken);
          }
          localStorage.setItem("keepMeLogin", JSON.stringify(keepMeLogin));
          // Persist token before any authenticated API calls — otherwise profile/settings
          // go out without Authorization, get 401, and the axios interceptor force-logs out.
          dispatch(login(initialUserData));

          let subscriptionAccess = apiSubscriptionAccess;
          let profileData = null;

          try {
            const userDataResponse = await userDataService.fetchUserData();
            profileData = userDataResponse.profileData;
            subscriptionAccess = resolveSubscriptionAccess(
              profileData,
              apiSubscriptionAccess
            );
            dispatch(login({
              ...initialUserData,
              profileData,
              settingsData: userDataResponse.settingsData,
              lastDataRefresh: userDataResponse.lastDataRefresh,
              subscriptionAccess,
            }));
          } catch (error) {
            console.error("Error fetching user data:", error);
            subscriptionAccess = resolveSubscriptionAccess(null, apiSubscriptionAccess);
            dispatch(login({
              ...initialUserData,
              subscriptionAccess,
            }));
          }

          if (subscriptionAccess?.isRestricted) {
            showErrorToast(
              "Subscription grace period has ended. Pay your invoice in Billing to restore full access."
            );
          } else if (subscriptionAccess?.isInGracePeriod) {
            const days = Math.max(1, subscriptionAccess.daysUntilRestriction);
            showErrorToast(
              `Subscription expired. ${days} day(s) remaining before access is limited. Please pay your invoice.`
            );
          } else {
            showSuccessToast("Logged in Successfully.");
          }

          const redirectTo = getPostLoginPath({
            location,
            searchParams,
            subscriptionAccess,
            user: { userData: staffUser, profileData },
          });
          navigate(redirectTo, { replace: true });
        }
        else
          showErrorToast(response.data.message);

        setLoading(false);

      });

    } catch (err) {
      showErrorToast(err?.response?.data?.message ?? "Something went wrong");
      setLoading(false);
    }
  };

  if (isAuthenticated) {
    return <Preloader />;
  }

  return (
    <div>
      {loading && (<Preloader />)}
      <style>{`
        @font-face {
          font-family: 'Sefon';
          src: url('/assets/fonts/Sefon-Regular.ttf') format('truetype');
          font-weight: 400;
          font-style: normal;
          font-display: swap;
        }
        .news-caption .caption-title {
          font-family: 'Sefon', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
          font-weight: bold;
          color: #ffffff !important;
          position: relative;
          display: inline-block;
          padding-bottom: 8px;
        }
        .news-caption .caption-title::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: -10px;
          right: -10px;
          height: 3px;
          background-color: #dc3545;
        }
        .news-caption .caption-title .rotated-e {
          display: inline-block;
          transform: rotate(-15deg);
          transform-origin: center;
        }
        .login-header .brand {
          font-family: 'Sefon', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
        }
        .login-header .brand small {
          font-family: 'Sefon', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
        }
        .login-container {
          background: #2d2d2d !important;
        }
        .login-content .form-control {
          background: #3d3d3d !important;
          border: 1px solid #4d4d4d !important;
          border-radius: 4px !important;
          color: #ffffff !important;
        }
        .login-content .form-control:focus {
          background: #3d3d3d !important;
          border-color: #5d5d5d !important;
          box-shadow: 0 0 0 0.2rem rgba(255, 255, 255, 0.1) !important;
          color: #ffffff !important;
        }
        .login-content .form-floating label {
          color: rgba(255, 255, 255, 0.7) !important;
        }
        .login-content .form-floating .form-control:focus ~ label,
        .login-content .form-floating .form-control:not(:placeholder-shown) ~ label {
          color: rgba(255, 255, 255, 0.9) !important;
        }
        .login-content .form-check-label {
          color: #ffffff !important;
        }
        .login-content .form-check-input {
          background-color: #3d3d3d !important;
          border-color: #4d4d4d !important;
        }
        .login-content .form-check-input:checked {
          background-color: #28a745 !important;
          border-color: #28a745 !important;
        }
        .login-content .btn-theme {
          background: #28a745 !important;
          border: none !important;
          border-radius: 4px !important;
          color: #ffffff !important;
          font-weight: 500 !important;
        }
        .login-content .btn-theme:hover {
          background: #218838 !important;
        }
        .login-content .text-theme {
          color: #28a745 !important;
        }
        .login-content .text-theme:hover {
          color: #218838 !important;
        }
        .login-content hr {
          border-color: rgba(255, 255, 255, 0.1) !important;
        }
        .login-content .text-gray-600 {
          color: rgba(255, 255, 255, 0.6) !important;
        }
      `}</style>
      <div className="login login-with-news-feed">
        <div className="news-feed">
          <div className="news-image" style={{ backgroundImage: 'url(/assets/img/login-bg/login-bg-19.png)' }}></div>
          <div className="news-caption">
            <h4 className="caption-title"><b>ALT<span className="rotated-e">e</span>POS</b></h4>
            <p>
            A complete system designed to streamline the operations of Restaurants, Pizza Shops, Bakeries, Coffee Shops & Retail Businesses through a powerful, comprehensive solution.
            </p>
          </div>
        </div>
        <div className="login-container">
          <div className="login-header mb-30px">
            <div className="brand">
              <div className="d-flex align-items-center">
              <img src='/assets/img/logo.png' height='40px' alt=" Logo " /> 
              </div>
              <small>A Professional Point-of-Sale Management System. </small>
            </div>
          </div>
          <div className="login-content">
            <form onSubmit={handleSubmit} className="fs-13px">
              <div className="form-floating mb-15px">
                <input
                  type="text"
                  className="form-control h-45px fs-13px"
                  placeholder="Email Address"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  id="emailAddress"
                />
                <label htmlFor="emailAddress" className="d-flex align-items-center fs-13px">Email Address</label>
              </div>
              <FloatingPasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <div className="form-check mb-30px">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={keepMeLogin}
                  onChange={(e) => setKeepMeLogin(e.target.checked)}
                  id="rememberMe"
                />
                <label className="form-check-label" htmlFor="rememberMe">
                  Remember Me
                </label>
              </div>
              <div className="mb-15px">
                <button type="submit" className="btn btn-theme d-block h-45px w-100 btn-lg fs-14px">Sign me in</button>
              </div>
              <div className="mb-40px pb-40px text-body">
                <Link className="text-theme">
                  Forget Password?
                </Link>
              </div>
              <hr className="bg-gray-600 opacity-2" />
              <div className="text-gray-600 text-center text-gray-500-darker mb-0">
                &copy; ALTePOS All Right Reserved {new Date().getFullYear()}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;